import React from "react";
import {
    FormFieldType,
    MetricsDashboardGraphMetric,
    MetricsDashboardGraphTypeAggregation,
    MetricsDashboardGraphTypeDisplayType,
} from "../../../__generated__/api";
import ReportFieldSelector, { useFlatFields } from "../../../components/pickers/field-selector/ReportFieldSelector";
import Select from "../../../components/select/Select";
import Button from "../../../components/theme/button/Button";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import Styles from "../Reports.module.scss";
import {
    GraphSettingsProperties,
    isNumeric,
} from "./base";

const DISPLAY_TYPES = {
    [MetricsDashboardGraphTypeDisplayType.Number]: "Number",
    [MetricsDashboardGraphTypeDisplayType.Currency]: "Currency",
    [MetricsDashboardGraphTypeDisplayType.Duration]: "Duration",
    [MetricsDashboardGraphTypeDisplayType.Float]: "Float",
    [MetricsDashboardGraphTypeDisplayType.Percentage]: "Percentage",
};

const AGGREGATION_NAME = {
    [MetricsDashboardGraphTypeAggregation.Average]: {
        label: "Average",
        isNumericOnly: true,
    },
    [MetricsDashboardGraphTypeAggregation.Median]: {
        label: "Median",
        isNumericOnly: true,
    },
    [MetricsDashboardGraphTypeAggregation.CountDistinct]: {
        label: "Count Distinct",
        isNumericOnly: false,
    },
    [MetricsDashboardGraphTypeAggregation.Sum]: {
        label: "Sum",
        isNumericOnly: true,
    },
    [MetricsDashboardGraphTypeAggregation.Count]: {
        label: "Count",
        isNumericOnly: false,
    },
    [MetricsDashboardGraphTypeAggregation.Max]: {
        label: "Max",
        isNumericOnly: true,
    },
    [MetricsDashboardGraphTypeAggregation.Min]: {
        label: "Min",
        isNumericOnly: true,
    },
};

export function MetricAggregation(props: GraphSettingsProperties & {index: number}) {
    const metric = props.graph.metrics?.[props.index];

    const { flatFields } = useFlatFields(props.graph.dataSource);
    const field = metric ? flatFields[metric.fieldId]?.column : undefined;

    let aggregations = Object.entries(AGGREGATION_NAME);

    if (!isNumeric(field?.field.type)) {
        aggregations = aggregations.filter(([, value]) => !value.isNumericOnly);
    }

    return (

        <FormGroup
            label="Function"
            required
            size="small"
            helpDisplay="icon"
        >
            <Select
                onChange={aggregation => {
                    const metricCopy = Array.from(props.graph.metrics ?? []);
                    metricCopy[props.index] = {
                        ...metricCopy[props.index],
                        aggregation: aggregation.value as MetricsDashboardGraphTypeAggregation,
                    };
                    delete (metricCopy[props.index] as {id?: string})?.id;
                    props.onChange({
                        ...props.graph,
                        metrics: metricCopy,
                    });
                }}
                value={metric ? ({
                    label: AGGREGATION_NAME[metric.aggregation]?.label,
                    value: metric.aggregation,
                }) : undefined}
                options={aggregations.map(([value, option]) => ({
                    label: option.label,
                    value,
                }))}
            />

        </FormGroup>
    );
}

export default function MetricProperties(props: GraphSettingsProperties) {
    const metric = props.graph.metrics?.firstElement;

    const { flatFields } = useFlatFields(props.graph.dataSource);
    const field = metric ? flatFields[metric.fieldId]?.column : undefined;
    const isFieldNumeric = isNumeric(field?.field.type);
    return (

        <>
            <FormGroup
                label="Value"
                required
                size="small"
                helpDisplay="icon"
                helpText="What value do you want displayed in the count"
            >
                <ReportFieldSelector
                    value={metric?.fieldId}
                    dataSource={props.graph.dataSource}
                    onChange={value => {
                        const metricCopy = { ...metric };
                        metricCopy.fieldId = value?.field.id;

                        if (isNumeric(value?.field.type)) {
                            metricCopy.display = MetricsDashboardGraphTypeDisplayType.Number;
                            metricCopy.aggregation = MetricsDashboardGraphTypeAggregation.Average;
                        } else {
                            metricCopy.aggregation = MetricsDashboardGraphTypeAggregation.Count;
                            delete metricCopy.display;
                        }
                        props.onChange({
                            ...props.graph,
                            metrics: [metricCopy as MetricsDashboardGraphMetric],
                        });
                    }}
                />
            </FormGroup>

            <FormGroup
                label="Function"
                required
                size="small"
                helpDisplay="icon"
                helpText="How do you want the report numbers aggregated?"
            >
                <div className={Styles["button-group"]}>
                    {Object.entries(AGGREGATION_NAME).map(([key, aggregation]) => (
                        (isFieldNumeric || aggregation.isNumericOnly === isFieldNumeric)
                        && (
                            <Button
                                size="small"
                                key={key}
                                variant={metric?.aggregation === key ? "filter" : "ghost-gray"}
                                className={metric?.aggregation === key ? "active" : ""}
                                onClick={() => {
                                    const metricCopy = { ...metric };
                                    metricCopy.aggregation = key as MetricsDashboardGraphTypeAggregation;
                                    props.onChange({
                                        ...props.graph,
                                        metrics: [metricCopy as MetricsDashboardGraphMetric],
                                    });
                                }}
                            >
                                {aggregation.label}
                            </Button>
                        )
                    ))}
                </div>
            </FormGroup>

            {isFieldNumeric && (
                <FormGroup
                    label="Display"
                    required
                    size="small"
                    helpDisplay="icon"
                    helpText="How do you want the number formatted"
                >
                    <div className={Styles["button-group"]}>
                        {Object.entries(DISPLAY_TYPES).map(([key, display]) => (
                            <Button
                                size="small"
                                key={key}
                                variant={metric?.display === key ? "outline-blue" : "ghost-gray"}
                                onClick={() => {
                                    const metricCopy = { ...metric };
                                    metricCopy.display = key as MetricsDashboardGraphTypeDisplayType;
                                    props.onChange({
                                        ...props.graph,
                                        metrics: [metricCopy as MetricsDashboardGraphMetric],
                                    });
                                }}
                            >
                                {display}
                            </Button>

                        ))}
                    </div>
                </FormGroup>
            )}
        </>
    );
}
